import * as ser from './SerUtil.js'
import { downloadTextFile } from '../SharedUtils.js'

/**
 * PdfStorage - Handles temporary storage of PDF data in Firebase Storage
 *
 * This class manages temporary PDF data files used for server-side PDF generation.
 * Firebase Storage is used instead of Firestore because it supports much larger files
 * (Firestore has a 1MB document limit, but PDF data can be larger).
 */
export class PdfStorage {
  constructor(firebaseApp) {
    this.firebaseApp = firebaseApp;
  }

  async savePdfDataToStorage(filename, dataString, metadata) {
    // Save PDF data to Firebase Storage for temporary storage
    // Storage supports much larger files than Firestore (which has 1MB limit)
    const { getStorage, ref, uploadString } = await import("firebase/storage");
    const storage = getStorage(this.firebaseApp);
    const storageRef = ref(storage, `temp-pdf-data/${filename}`);

    // Upload as a string (data URL format)
    await uploadString(storageRef, dataString, 'raw', {
      contentType: 'application/json',
      customMetadata: metadata
    });

    console.log(`Saved PDF data to Storage: temp-pdf-data/${filename}`);
  }

  async deletePdfDataFromStorage(filename) {
    // Delete temporary PDF data from Firebase Storage
    const { getStorage, ref, deleteObject } = await import("firebase/storage");
    const storage = getStorage(this.firebaseApp);
    const storageRef = ref(storage, `temp-pdf-data/${filename}`);
    await deleteObject(storageRef);
    console.log(`Deleted PDF data from Storage: temp-pdf-data/${filename}`);
  }

  async getPdfDataFromStorage(filename) {
    // Retrieve PDF data from Firebase Storage
    const { getStorage, ref, getDownloadURL } = await import("firebase/storage");
    const storage = getStorage(this.firebaseApp);
    const storageRef = ref(storage, `temp-pdf-data/${filename}`);

    // Get the download URL and fetch the data
    const url = await getDownloadURL(storageRef);
    const response = await fetch(url);
    const dataString = await response.text();
    return dataString;
  }

  /**
   * Generate a PDF report using server-side rendering
   *
   * @param {Object} options - Configuration options
   * @param {Object} options.data - The serializable data object to render
   * @param {string} options.route - The PDF report route (e.g., '/reports/house-results-pdf')
   * @param {Object} options.metadata - Metadata to attach to the storage file
   * @param {string} options.outputFilename - The desired PDF filename (without extension)
   * @param {Object} options.modal - The modal ref to show/hide during generation
   * @param {Object} options.gApp - Reference to the global app instance
   * @returns {Promise<void>}
   */
  static async generateServerPdf(options) {
    const { data, route, metadata, outputFilename, modal, gApp } = options;

    if (!data) {
      gApp.toastError("No data available to generate PDF");
      return;
    }

    let tempFilename = null;

    try {
      // Show the generating modal
      modal.value.showModal();

      // Save PDF data to Firebase Storage
      const pdfData = ser.writeToJson(data);
      const pdfDataString = JSON.stringify(pdfData);
      tempFilename = `temp-pdf-${Date.now()}-${Math.random().toString(36).substring(7)}.json`;

      console.log(`Saving PDF data to Storage: ${tempFilename} (${pdfDataString.length} bytes)`);
      await gApp.savePdfDataToStorage(tempFilename, pdfDataString, metadata);

      // Call the server to generate the PDF, passing the Storage filename
      const pdfBlob = await gApp.fetchFromServer('generate-pdf', 'POST', {
        route: route,
        dataId: tempFilename
      }, { responseType: 'blob' });

      const filename = `${outputFilename.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      downloadTextFile(pdfBlob, filename, { fileType: 'application/pdf' });
      gApp.toast(`PDF Ready!`, { type: 'success' });

      // Only clean up on success. On failure, Puppeteer may still be running
      // inside the server and reading the file — deleting it early is what
      // causes the "object not found" error in the PDF report component.
      try {
        await gApp.deletePdfDataFromStorage(tempFilename);
      } catch (cleanupError) {
        console.error("Error cleaning up PDF data:", cleanupError);
      }
    } catch (error) {
      console.error("Error generating PDF report:", error);
      gApp.reportError(error);
      gApp.toastError(`Error generating PDF: ${error.message}`);
      // Intentionally not deleting tempFilename on failure — Puppeteer may
      // still be mid-flight on the server and needs the file to exist.
      // Orphaned files have random names and are harmless.
    } finally {
      modal.value.closeModal();
    }
  }
}
